import { Box, Checkbox, Group, Text, TextInput, UnstyledButton } from "@mantine/core"
import { CaretRightIcon, MagnifyingGlassIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import treeClasses from "./category-tree-filter.module.css"
import classes from "./product-list/catalog.module.css"
import { cleanName } from "./product-list/utils"

// Category list of the buyer catalogue (canvas OrdB_Catalog / OrdB_CatalogFilters).
//
// Built from the products themselves rather than the supplier taxonomy API:
// every catalogue row carries its whole chain (itemType -> classification ->
// subClassification -> category), so the tree holds exactly the branches the
// buyer can order from, each with its product count, and needs no extra
// request. Several nodes can be ticked (OR'ed); the product filter matches
// each node's id at whichever level it sits (matchesCategorySelection).

// The backend item_type has no sort column; this is the business-preferred root order.
const ROOT_ITEM_TYPE_ORDER = [
  "зурагт, аудио видео",
  "компьютер, it бүтээгдэхүүн",
  "гар утас, таблет, дагалдах хэрэгсэл",
  "том цахилгаан хэрэгсэл",
  "жижиг цахилгаан хэрэгсэл",
  "гэр ахуйн тавилга",
  "оффисын тавилга",
  "гэр ахуйн бүтээгдэхүүн",
]

const rootOrderIndex = (name: string): number => {
  const idx = ROOT_ITEM_TYPE_ORDER.indexOf(name.trim().toLowerCase())
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx
}

type Node = {
  id: string
  name: string
  count: number
  children: Node[]
}

const LEVELS = ["itemType", "classification", "subClassification", "category"] as const

const buildTree = (products: any[]): Node[] => {
  const roots: Node[] = []
  const index = new Map<string, Node>()
  for (const product of products) {
    let siblings = roots
    let parentKey = ""
    for (const level of LEVELS) {
      const ref = product?.[level]
      if (!ref?.id) continue
      const name = cleanName(ref.name)
      // «Задгай» is the loose-items bucket; its products roll up into the parent.
      if (!name || name.toLowerCase() === "задгай") continue
      const key = `${parentKey}/${ref.id}`
      let node = index.get(key)
      if (!node) {
        node = { id: ref.id, name, count: 0, children: [] }
        index.set(key, node)
        siblings.push(node)
      }
      node.count++
      siblings = node.children
      parentKey = key
    }
  }
  const sortLevel = (nodes: Node[], root: boolean) => {
    nodes.sort((a, b) => (root ? rootOrderIndex(a.name) - rootOrderIndex(b.name) : 0) || a.name.localeCompare(b.name))
    nodes.forEach((n) => sortLevel(n.children, false))
  }
  sortLevel(roots, true)
  return roots
}

const filterTree = (nodes: Node[], query: string): Node[] => {
  if (!query) return nodes
  const q = query.toLowerCase()
  return nodes
    .map((n) => {
      if (n.name.toLowerCase().includes(q)) return n
      const children = filterTree(n.children, query)
      return children.length > 0 ? { ...n, children } : null
    })
    .filter((n): n is Node => n !== null)
}

const pathTo = (nodes: Node[], id: string, trail: string[] = []): string[] | null => {
  for (const n of nodes) {
    if (n.id === id) return trail
    const found = pathTo(n.children, id, [...trail, n.id])
    if (found) return found
  }
  return null
}

/** One ticked node: its id (matched at whichever level it sits) and the names down to it. */
export type CategorySelection = { id: string; path: string[] }

type Props = {
  /** Products the counts are taken from (every other filter already applied). */
  products: any[]
  total: number
  selected: CategorySelection[]
  onChange: (selection: CategorySelection[]) => void
}

/**
 * Multi-select tree, the same as the inventory-admin catalog (Itm_CatalogFilters): a chevron opens a
 * node, the checkbox row ticks it, ticked nodes are OR'ed. Ticking also opens the node so it can be
 * narrowed further. The branch holding a ticked node reads bold, so a repeated name shows its parent.
 */
export function CategoryTreeFilter({ products, total, selected, onChange }: Props) {
  const { t } = useTranslation("order")
  const [query, setQuery] = React.useState("")
  const tree = React.useMemo(() => buildTree(products), [products])
  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set())

  // Open the branches that hold the selection (e.g. after coming back from a product page).
  React.useEffect(() => {
    const open = selected.flatMap((s) => pathTo(tree, s.id) || [])
    if (open.length > 0) setExpanded((prev) => new Set([...Array.from(prev), ...open]))
  }, [selected, tree])

  const visible = React.useMemo(() => filterTree(tree, query.trim()), [tree, query])
  const searching = query.trim().length > 0
  const selectedIds = React.useMemo(() => new Set(selected.map((s) => s.id)), [selected])

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const tick = (node: Node, path: string[]) => {
    if (selectedIds.has(node.id)) {
      onChange(selected.filter((s) => s.id !== node.id))
      return
    }
    onChange([...selected, { id: node.id, path }])
    if (node.children.length > 0) setExpanded((prev) => new Set(prev).add(node.id))
  }

  const holdsSelection = (node: Node): boolean => node.children.some((c) => selectedIds.has(c.id) || holdsSelection(c))

  const renderNodes = (nodes: Node[], depth: number, parentPath: string[]): React.ReactNode =>
    nodes.map((node) => {
      const open = searching || expanded.has(node.id)
      const hasChildren = node.children.length > 0
      const isSelected = selectedIds.has(node.id)
      const onPath = !isSelected && holdsSelection(node)
      const path = [...parentPath, node.name]
      return (
        <React.Fragment key={`${depth}-${node.id}`}>
          <Box className={treeClasses.nodeRow} data-selected={isSelected || undefined} pl={depth * 16}>
            {hasChildren ? (
              <UnstyledButton
                className={treeClasses.chevron}
                aria-label={open ? t("catalog.categoryCollapseNode") : t("catalog.categoryExpandNode")}
                aria-expanded={open}
                disabled={searching}
                onClick={() => toggle(node.id)}
              >
                <CaretRightIcon size={14} data-open={open || undefined} />
              </UnstyledButton>
            ) : (
              <Box className={treeClasses.chevronSpacer} />
            )}
            <UnstyledButton className={treeClasses.nodeLabel} onClick={() => tick(node, path)} aria-pressed={isSelected}>
              <Checkbox size="sm" color="dark" checked={isSelected} readOnly tabIndex={-1} aria-hidden className={treeClasses.check} />
              <Text
                span
                fz="var(--nw-fz-body)"
                lh={1.35}
                fw={isSelected || onPath ? 600 : depth === 0 ? 500 : 400}
                c={isSelected || onPath ? "var(--nw-text)" : "var(--nw-text-2)"}
              >
                {node.name}
              </Text>
              <Text span className={treeClasses.count}>
                {node.count}
              </Text>
            </UnstyledButton>
          </Box>
          {hasChildren && open && renderNodes(node.children, depth + 1, path)}
        </React.Fragment>
      )
    })

  const none = selected.length === 0

  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Text className={classes.sectionTitle}>{t("catalog.categoryTitle")}</Text>
        {expanded.size > 0 && (
          <UnstyledButton className={classes.linkButton} onClick={() => setExpanded(new Set())}>
            {t("catalog.collapse")}
          </UnstyledButton>
        )}
      </Group>
      <Text className={classes.meta} mb="xs">
        {t("catalog.categoryLevels")}
      </Text>
      {tree.length > 8 && (
        <TextInput
          mb="xs"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
          placeholder={t("catalog.categorySearch")}
          aria-label={t("catalog.categorySearch")}
          leftSection={<MagnifyingGlassIcon size={16} />}
        />
      )}
      <Box className={treeClasses.nodeRow} data-selected={none || undefined}>
        <Box className={treeClasses.chevronSpacer} />
        <UnstyledButton className={treeClasses.nodeLabel} onClick={() => onChange([])} aria-pressed={none}>
          <Text span fz="var(--nw-fz-body)" lh={1.35} fw={none ? 600 : 500} c={none ? "var(--nw-text)" : "var(--nw-text-2)"}>
            {t("catalog.categoryAll")}
          </Text>
          <Text span className={treeClasses.count}>
            {total}
          </Text>
        </UnstyledButton>
      </Box>
      {visible.length > 0 ? (
        renderNodes(visible, 0, [])
      ) : (
        <Text className={classes.meta} px={8} py="xs">
          {t("catalog.categoryEmpty")}
        </Text>
      )}
    </Box>
  )
}
